import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Goal } from '../../types/work';
import { WorkStatusBadge } from './WorkStatusBadge';
import { PriorityChip } from './PriorityChip';
import { ProgressBar } from './ProgressBar';
import { DueDateLabel } from './DueDateLabel';
import UserBadge from '../common/UserBadge';

interface Props {
  goal: Goal;
  onClick: () => void;
}

export const GoalCard: React.FC<Props> = ({ goal, onClick }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  return (
    <div
      onClick={onClick}
      className={`rounded-xl border overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.01] hover:-translate-y-px ${
        darkMode
          ? 'bg-gray-800 border-gray-700 hover:shadow-lg hover:shadow-emerald-500/10'
          : 'bg-white border-gray-200 hover:shadow-lg hover:shadow-emerald-500/10'
      } ${goal.status === 'completed' ? 'ring-2 ring-emerald-400/40' : ''}`}
    >
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-emerald-900/60 to-emerald-800/30 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white text-lg shrink-0">
            {goal.icon || '🎯'}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white truncate">{goal.title}</h3>
            {goal.description && <p className="text-emerald-200/70 text-xs truncate">{goal.description}</p>}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <WorkStatusBadge status={goal.status} />
          <PriorityChip priority={goal.priority} />
          <DueDateLabel date={goal.due_date} />
        </div>

        <ProgressBar pct={0} accent="emerald" shimmer={goal.status === 'completed'} />

        <div className="flex items-center justify-between">
          {/* Project pills — click navigates to filtered projects */}
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/projects?goal=${goal.id}`); }}
            className={`text-xs hover:underline ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}
          >
            {goal.project_count ?? 0} project{(goal.project_count ?? 0) !== 1 ? 's' : ''} →
          </button>
          <UserBadge owner={goal.owner} size="sm" />
        </div>
      </div>
    </div>
  );
};
